import { resume } from "../content/resume";
import "../styles/resume.css";

/** Renders the reviewed resume as accessible HTML within the site's single main landmark, with the original PDF as a secondary representation. */
export default function ResumePage() {
  return (
    <article className="resume-page">
      <header className="resume-hero">
        <p className="eyebrow">Resume</p>
        <h1>{resume.name}</h1>
        <p className="resume-headline">{resume.headline}</p>
        <p className="resume-profile">{resume.profile}</p>

        <ul className="resume-contact-list" aria-label="Contact information">
          {resume.contact.map((item) => {
            const external = item.href.startsWith("http");

            return (
              <li key={item.label}>
                <span>{item.label}</span>
                <a
                  href={item.href}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noreferrer" : undefined}
                >
                  {item.value}
                </a>
              </li>
            );
          })}
        </ul>

        <div className="resume-actions">
          <a
            className="button"
            href={resume.pdfPath}
            target="_blank"
            rel="noreferrer"
          >
            View PDF ↗
          </a>
        </div>
      </header>

      <div className="resume-layout">
        <div className="resume-main-column">
          <section
            className="resume-section"
            aria-labelledby="professional-experience"
          >
            <p className="eyebrow">Experience</p>
            <h2 id="professional-experience">Professional Experience</h2>

            <div className="resume-entry-list">
              {resume.experience.map((entry) => (
                <article
                  className="resume-entry"
                  key={`${entry.role}-${entry.organization}`}
                >
                  <header>
                    <div>
                      <h3>{entry.role}</h3>
                      <p>{entry.organization}</p>
                    </div>
                    <span>{entry.dates}</span>
                  </header>

                  <ul>
                    {entry.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </section>

          <section
            className="resume-section"
            aria-labelledby="selected-engineering-projects"
          >
            <p className="eyebrow">Selected work</p>
            <h2 id="selected-engineering-projects">
              Selected Engineering Projects
            </h2>

            <div className="resume-entry-list">
              {resume.projects.map((project) => (
                <article
                  className="resume-entry resume-project"
                  key={project.name}
                >
                  <header>
                    <h3>{project.name}</h3>
                    <span>{project.technologies}</span>
                  </header>

                  <ul>
                    {project.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </section>
        </div>

        <aside
          className="resume-sidebar"
          aria-label="Resume supporting details"
        >
          <section className="resume-sidebar-section">
            <h2>Technical Expertise</h2>

            {resume.technicalExpertise.map((group) => (
              <div className="resume-sidebar-group" key={group.title}>
                <h3>{group.title}</h3>
                <p>{group.detail}</p>
              </div>
            ))}
          </section>

          <section className="resume-sidebar-section">
            <h2>Systems Integration</h2>

            {resume.systemsIntegration.map((group) => (
              <div className="resume-sidebar-group" key={group.title}>
                <h3>{group.title}</h3>
                <p>{group.detail}</p>
              </div>
            ))}
          </section>

          <section className="resume-sidebar-section">
            <h2>References</h2>

            <div className="resume-reference-list">
              {resume.references.map((reference) => (
                <address className="resume-reference" key={reference.name}>
                  <strong>{reference.name}</strong>
                  <a href={reference.phoneHref}>{reference.phone}</a>
                  <a href={reference.emailHref}>{reference.email}</a>
                </address>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </article>
  );
}
